import type { VoiceSpace } from '../../content/voices/voice-spaces.js';
import { AudioEngine } from './audio-engine.js';
import { decibels, EffectVoices, HERO_VOICE_DB, MIX, type SoundKind } from './mix.js';

interface MixLevels { master: number; music: number; sfx: number; voices: number; /** Percent of each hero voice event's own odds: 100 is as designed, 0 is silent. */ voiceFrequency: number; muted: boolean }
const LATE_MS = 300;
const KEY = 'nimblez.audio', DEFAULTS: MixLevels = { master: 100, music: 50, sfx: 80, voices: 100, voiceFrequency: 100, muted: false };

function load(): MixLevels {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; } catch { return { ...DEFAULTS }; }
}

/** Master, music and sound-effect levels (0 to 100) and a mute switch, kept on this device and shared by everything that makes sound. */
export class AudioMixer {
  /** The page's one audio graph; the levels below drive its buses. */
  readonly engine = new AudioEngine();
  preloadSfx(urls: string[]) { return this.engine.preload(urls); }
  sfxDuration(url: string) { return this.engine.duration(url); }
  private levels = load();
  private listeners = new Set<() => void>();
  private stopSpoken: (() => void) | undefined;
  stopVoice() { this.stopSpoken?.(); this.stopSpoken = undefined; }
  constructor() { this.apply(); }
  private apply() { this.engine.setLevels({ music: this.musicGain, effects: this.sfxGain, voices: this.voiceGain }); }

  get master() { return this.levels.master; }
  get music() { return this.levels.music; }
  get sfx() { return this.levels.sfx; }
  get voices() { return this.levels.voices; }
  get voiceFrequency() { return this.levels.voiceFrequency; }
  get muted() { return this.levels.muted; }
  /** Final 0..1 volume for the music track. */
  get musicGain() { return this.levels.muted ? 0 : this.levels.master / 100 * this.levels.music / 100; }
  /** Final 0..1 volume for effects at full strength. */
  get sfxGain() { return this.levels.muted ? 0 : this.levels.master / 100 * this.levels.sfx / 100; }

  /** Final 0..1 volume for hero voices at full strength. */
  get voiceGain() { return this.levels.muted ? 0 : this.levels.master / 100 * this.levels.voices / 100; }

  set(change: Partial<MixLevels>) {
    this.levels = { ...this.levels, ...change };
    try { localStorage.setItem(KEY, JSON.stringify(this.levels)); } catch { /* The mix then lasts for this visit only. */ }
    this.apply();
    for (const listener of this.listeners) listener();
  }

  /** Calls `listener` whenever a level or the mute switch changes. */
  onChange(listener: () => void) { this.listeners.add(listener); }

  /** Plays a spoken line at `strength` (0..1) of the voice volume, `pan` from -1 (left) to 1 (right). Returns how many seconds it lasts, or 0 when nothing played. */
  playVoice(url: string, strength = 1, pan = 0, space?: VoiceSpace, rate = 1): number {
    if (this.voiceGain <= 0) return 0;
    this.stopVoice();
    const length = this.engine.duration(url) / rate;
    if (length) {
      const stop = this.engine.play(url, this.spoken(strength, pan, space, rate));
      if (!stop) return 0;
      this.stopSpoken = stop; return length;
    }
    // Not decoded yet (a first Listen in the creator): the line plays once it is, unless something stops it first; its length is a guess.
    let cancelled = false;
    this.stopSpoken = () => { cancelled = true; };
    void this.engine.decode(url).then(() => {
      if (cancelled) return;
      const stop = this.engine.play(url, this.spoken(strength, pan, space, rate));
      if (stop) this.stopSpoken = stop;
    });
    return 2;
  }

  private spoken(strength: number, pan: number, space?: VoiceSpace, rate = 1) { return { bus: 'voices' as const, volume: strength * decibels(HERO_VOICE_DB), pan, rate, matched: true, space }; }

  private readonly effects = new EffectVoices();
  /**
   * Plays a battle sound of `kind` at `strength` (its kind's level, distance and any accent already applied) against the matched
   * loudness, if the kind's cap and the overall limit leave room for it (`mix.ts`). A sound not decoded yet is skipped: a blow heard late is wrong.
   */
  playEffect(url: string, kind: SoundKind, strength: number, rate = 1, pan = 0) {
    const seconds = this.engine.duration(url) / rate;
    if (this.sfxGain <= 0 || !seconds) { if (!seconds) void this.engine.decode(url); return; }
    const now = performance.now() / 1000;
    if (!this.effects.admit(kind, now)) return;
    const stop = this.engine.play(url, { volume: strength, rate, pan, matched: true, space: MIX[kind].space });
    if (stop) this.effects.add(kind, now, seconds, stop);
  }

  /** Plays a preloaded effect with a hall reverb tail. */
  playReverb(url: string, strength = 1, rate = 1, fade?: { in: number; out: number }) {
    this.playSfx(url, strength, fade, rate, true);
  }

  /** Plays a one-shot effect at `strength` (0..1) of the current effects volume, optionally fading in and out over the given seconds. `start` skips into the file and `length` cuts the sound after that many seconds; `pan` (-1 left to 1 right) places it across the screen. */
  playSfx(url: string, strength = 1, fade?: { in: number; out: number; start?: number; length?: number }, rate = 1, reverb = false, pan = 0) {
    if (this.sfxGain <= 0) return;
    const options = { volume: strength, rate, reverb, fade, pan };
    if (this.engine.play(url, options) || this.engine.duration(url)) return;
    // Not decoded yet: a cue is feedback for a moment, so it plays only if it is ready almost at once.
    const asked = performance.now();
    void this.engine.decode(url).then(() => { if (performance.now() - asked <= LATE_MS) this.engine.play(url, options); });
  }
}

export const mixer = new AudioMixer();
